import React, { useState, useEffect } from'react';
import { useParams, Link } from'react-router-dom';
import { motion } from'framer-motion';
import { ChevronLeft, Mail, Phone, MapPin, Users, Calendar, ArrowRight, Clock, Globe, Facebook, Instagram, Twitter } from'lucide-react';
import { Button } from'../components/ui/button';
import { Badge } from'../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from'../components/ui/avatar';

import { API_URL } from'../utils/api';

export default function InstructorProfilePage() {
 const { id } = useParams();
 const [instructor, setInstructor] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 fetchInstructor();
 }, [id]);

 const fetchInstructor = async () => {
 setLoading(true);
 try {
 const response = await fetch(`${API_URL}/api/instructors/${id}`);
 if (response.ok) {
 const data = await response.json();
 setInstructor(data);
 }
 } catch (error) {
 console.error('Error fetching instructor:', error);
 } finally {
 setLoading(false);
 }
 };

 if (loading) {
 return (
 <div className="min-h-screen bg-slate-50 flex items-center justify-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
 </div>
 );
 }

 if (!instructor) {
 return (
 <div className="min-h-screen bg-slate-50 flex items-center justify-center">
 <div className="text-center">
 <h1 className="text-2xl font-bold text-slate-700 mb-4">Instructor not found</h1>
 <Link to="/instructors">
 <Button className="bg-violet-600 hover:bg-violet-500">Back to Instructors</Button>
 </Link>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-slate-50 transition-colors duration-200" data-testid="instructor-profile-page">
 {/* Header */}
 <div className="bg-gradient-to-br from-violet-600 to-purple-700">
 <div className="container mx-auto px-6 lg:px-12 py-8">
 <Link to="/instructors">
 <Button variant="secondary" className="bg-white/90 backdrop-blur-sm mb-6" data-testid="back-btn">
 <ChevronLeft className="w-4 h-4 mr-1" />
 Back to Instructors
 </Button>
 </Link>
 </div>
 </div>

 {/* Main Content */}
 <div className="container mx-auto px-6 lg:px-12 -mt-8 relative z-10 pb-12">
 <div className="grid lg:grid-cols-3 gap-8">
 {/* Left Column - Profile Card */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 className="lg:col-span-1"
 >
 <div className="bg-white rounded-2xl shadow-xl p-6 text-center">
 <Avatar className="w-32 h-32 mx-auto mb-4 border-4 border-violet-100">
 <AvatarImage src={instructor.image_url} alt={instructor.name} />
 <AvatarFallback className="bg-violet-100 text-violet-600 text-3xl font-bold">
 {instructor.name.split('').map(n => n[0]).join('')}
 </AvatarFallback>
 </Avatar>
 
 <h1 className="text-2xl font-extrabold text-slate-900 mb-2" style={{ fontFamily:'Syne, sans-serif' }} data-testid="instructor-name">
 {instructor.name}
 </h1>
 
 <Badge className="bg-violet-100 text-violet-700 mb-4">
 <Users className="w-3 h-3 mr-1" />
 Line Dance Instructor
 </Badge>

 {/* Years Experience */}
 {instructor.years_experience && (
 <p className="text-sm text-slate-600 flex items-center justify-center mb-4">
 <Clock className="w-4 h-4 mr-1 text-violet-500" />
 {instructor.years_experience} years experience
 </p>
 )}

 {/* Contact Info */}
 <div className="space-y-3 mt-6 text-left">
 {instructor.email && (
 <a 
 href={`mailto:${instructor.email}`} 
 className="flex items-center gap-3 text-slate-600 hover:text-violet-600 transition-colors p-3 bg-slate-50 rounded-xl"
 data-testid="instructor-email"
 >
 <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
 <Mail className="w-5 h-5 text-violet-600" />
 </div>
 <span className="text-sm">{instructor.email}</span>
 </a>
 )}
 {instructor.phone && (
 <a 
 href={`tel:${instructor.phone}`} 
 className="flex items-center gap-3 text-slate-600 hover:text-violet-600 transition-colors p-3 bg-slate-50 rounded-xl"
 data-testid="instructor-phone"
 >
 <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
 <Phone className="w-5 h-5 text-violet-600" />
 </div>
 <span className="text-sm">{instructor.phone}</span>
 </a>
 )}
 </div>

 {/* Social Links */}
 {instructor.social_links && Object.keys(instructor.social_links).some(k => instructor.social_links[k]) && (
 <div className="mt-6 pt-4 border-t border-slate-200">
 <p className="text-xs text-slate-400 mb-3">Connect</p>
 <div className="flex justify-center gap-3">
 {instructor.social_links.facebook && (
 <a href={instructor.social_links.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 hover:bg-blue-200 transition-colors">
 <Facebook className="w-5 h-5" />
 </a>
 )}
 {instructor.social_links.instagram && (
 <a href={instructor.social_links.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center text-pink-600 hover:bg-pink-200 transition-colors">
 <Instagram className="w-5 h-5" />
 </a>
 )}
 {instructor.social_links.twitter && (
 <a href={instructor.social_links.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center text-sky-600 hover:bg-sky-200 transition-colors">
 <Twitter className="w-5 h-5" />
 </a>
 )}
 {instructor.social_links.website && (
 <a 
 href={instructor.social_links.website.startsWith('http') ? instructor.social_links.website : `https://${instructor.social_links.website}`} 
 target="_blank" 
 rel="noopener noreferrer" 
 className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-200 transition-colors"
 title={instructor.social_links.website.replace(/^https?:\/\//,'')}
 >
 <Globe className="w-5 h-5" />
 </a>
 )}
 </div>
 </div>
 )}
 </div>
 </motion.div>

 {/* Right Column - Bio & Details */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.1 }}
 className="lg:col-span-2 space-y-6"
 >
 {/* About Section */}
 <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
 <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily:'Syne, sans-serif' }}>
 About
 </h2>
 <p className="text-slate-600 text-lg leading-relaxed whitespace-pre-wrap" data-testid="instructor-bio">
 {instructor.bio ||'No bio available.'}
 </p>
 </div>

 {/* Specialties Section */}
 {instructor.specialties && instructor.specialties.length > 0 && (
 <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
 <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily:'Syne, sans-serif' }}>
 Specialties
 </h2>
 <div className="flex flex-wrap gap-2">
 {instructor.specialties.map((specialty, i) => (
 <Badge 
 key={i} 
 className="bg-violet-100 text-violet-700 px-4 py-2 text-sm"
 >
 {specialty}
 </Badge>
 ))}
 </div>
 </div>
 )}

 {/* Linked Clubs Section */}
 {instructor.clubs && instructor.clubs.length > 0 && (
 <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
 <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily:'Syne, sans-serif' }}>
 Teaching At
 </h2>
 <div className="space-y-3">
 {instructor.clubs.map((club, index) => (
 <Link key={club.club_id || index} to={`/clubs/${club.slug || club.club_id}`} data-testid={`linked-club-${club.club_id}`}>
 <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-violet-50 transition-colors group">
 <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-200 flex-shrink-0">
 {club.image_url ? (
 <img 
 src={club.image_url} 
 alt={club.name}
 className="w-full h-full object-cover"
 />
 ) : (
 <div className="w-full h-full flex items-center justify-center">
 <Users className="w-6 h-6 text-slate-400" />
 </div>
 )}
 </div>
 <div className="flex-1 min-w-0">
 <h3 className="font-bold text-slate-900 group-hover:text-violet-600 transition-colors">
 {club.name}
 </h3>
 <p className="text-sm text-slate-400 flex items-center mt-1">
 <MapPin className="w-4 h-4 mr-1" />
 {club.location || [club.city, club.region].filter(Boolean).join(',') ||'Location not specified'}
 </p>
 </div>
 <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-violet-500 transition-colors" />
 </div>
 </Link>
 ))}
 </div>
 </div>
 )}

 {/* Member Since */}
 <div className="bg-slate-100 rounded-xl p-4 flex items-center gap-3">
 <Calendar className="w-5 h-5 text-violet-500" />
 <span className="text-sm text-slate-600">
 Member since {new Date(instructor.created_at).toLocaleDateString('en-GB', { month:'long', year:'numeric' })}
 </span>
 </div>
 </motion.div>
 </div>
 </div>
 </div>
 );
}
